import React from "react";
import DocumentUpload from "@components/FileUpload/FileUpload";
import { useFileUploadProfiles } from "@components/FileUpload/FileUpload.constants";
import SingleFile from "@components/FileUpload/SingleFile";
import FormItem from "@components/FormItem/FormItem";
import SideDrawer from "@components/SimpleSideDrawer/SideDrawer";
import { useDeficiencyDocumentUpload } from "@features/deficiencies/hooks/useDeficiencyDocumentUpload";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  approveDeficiencySchema,
  MAX_APPROVE_DEFICIENCY_COMMENTS_LENGTH,
  type ApproveDeficiencyFormData,
} from "./approveDeficiencySchema";
import { DEFICIENCY_APPROVE_ATTACHMENTS_UPLOAD_KEY } from "./uploadKeys";

interface ApproveDeficiencyDrawerProps {
  isOpen: boolean;
  deficiencyId?: string;
  onClose: (drawerName: "approveDrawer") => void;
  onSubmit?: (payload: {
    deficiencyId?: string;
    comments?: string;
    attachments: {
      documentUrl: string;
      documentName?: string;
    }[];
  }) => Promise<void> | void;
  isSubmitting?: boolean;
}

const DRAWER_NAME = "approveDrawer";
const ATTACHMENT_FIELD_NAME = DEFICIENCY_APPROVE_ATTACHMENTS_UPLOAD_KEY;
const MAX_ATTACHMENTS = 10;
const APPROVAL_ATTACHMENT_DOCUMENT_TYPE = "APPROVAL_NOTES";

const ApproveDeficiencyDrawer: React.FC<ApproveDeficiencyDrawerProps> = ({
  isOpen,
  deficiencyId,
  onClose,
  onSubmit,
  isSubmitting,
}) => {
  const { t } = useTranslation();

  const schema = React.useMemo(() => approveDeficiencySchema(t), [t]);
  const form = useForm<ApproveDeficiencyFormData>({
    resolver: zodResolver(schema),
    mode: "onChange",
    defaultValues: {
      comments: "",
    },
  });

  const { handleSubmit, reset } = form;

  const fileProfiles = useFileUploadProfiles();
  const attachmentProfile = fileProfiles.sideDrawerApprovalFile;
  const {
    uploadEntries: attachments,
    setUploadEntries: setAttachments,
    handleUpload,
    resetUpload,
    uploadedDocumentUrls,
    isUploading,
  } = useDeficiencyDocumentUpload({
    storeKey: ATTACHMENT_FIELD_NAME,
    certificateType: APPROVAL_ATTACHMENT_DOCUMENT_TYPE,
    fallbackFileNamePrefix: "approval-attachment",
    errorNotification: {
      titleKey: "deficiencies.notifications.approve-error-title",
      descriptionKey: "deficiencies.notifications.approve-error-description",
    },
  });

  const allowedExtensions = attachmentProfile.format.map((ext) =>
    ext.startsWith(".") ? ext.toLowerCase() : `.${ext.toLowerCase()}`,
  );

  const resetForm = React.useCallback(() => {
    reset({ comments: "" });
    resetUpload();
  }, [reset, resetUpload]);

  const handleDrawerClose = React.useCallback(() => {
    onClose(DRAWER_NAME);
  }, [onClose]);

  const handleDrawerCancel = React.useCallback(() => {
    resetForm();
    onClose(DRAWER_NAME);
  }, [onClose, resetForm]);

  const handleRemoveAttachment = React.useCallback((index: number) => {
    setAttachments((prev) => prev.filter((_, currentIndex) => currentIndex !== index));
  }, []);

  const handleSubmitForm = React.useCallback(
    async (data: ApproveDeficiencyFormData) => {
      await onSubmit?.({
        deficiencyId,
        comments: data.comments.trim() || undefined,
        attachments: uploadedDocumentUrls.map((documentUrl, index) => ({
          documentUrl,
          documentName: attachments[index]?.name,
        })),
      });
      resetForm();
    },
    [attachments, deficiencyId, onSubmit, resetForm, uploadedDocumentUrls],
  );

  if (!isOpen) {
    return null;
  }

  return (
    <div data-testid="approve-deficiency-drawer-form" className="w-[400px] max-w-[100vw]">
      <SideDrawer
        isOpen={isOpen}
        title={t("deficiencies.decision-drawer.approve-title")}
        onClose={handleDrawerClose}
        onCancel={handleDrawerCancel}
        submitLabel={t("deficiencies.approve-button")}
        cancelLabel={t("common.cancel")}
        onSubmit={handleSubmit(handleSubmitForm)}
        isSubmitting={Boolean(isSubmitting || isUploading)}
      >
        <div className="flex-1 w-full py-2">
          <div className="flex flex-col gap-4 px-6">
            <FormProvider {...form}>
              <FormItem<ApproveDeficiencyFormData, "comments">
                name="comments"
                type="textarea"
                label={t("deficiencies.decision-drawer.comments")}
                validationLabel={t("deficiencies.decision-drawer.comments")}
                testId="approve-deficiency-comments"
                maxChars={MAX_APPROVE_DEFICIENCY_COMMENTS_LENGTH}
                showCharCounter
                minRows={4}
              />
            </FormProvider>

            <DocumentUpload
              label={t("deficiencies.decision-drawer.attachments")}
              testId="approve-deficiency-attachment-upload"
              maxFiles={MAX_ATTACHMENTS}
              allowedExtensions={allowedExtensions}
              name={ATTACHMENT_FIELD_NAME}
              onUpload={handleUpload}
              files={attachments}
              setFiles={setAttachments}
              conditions={attachmentProfile}
              uploadDescription={t("deficiencies.decision-drawer.upload-description")}
              disabled={Boolean(isSubmitting || isUploading)}
            />

            {attachments.length > 0 && (
              <div className="flex flex-col gap-4">
                {attachments.map((file, index) => (
                  <SingleFile
                    key={`${file.name}-${file instanceof File ? file.lastModified : index}`}
                    testId={`approve-deficiency-attachment-${index}`}
                    file={file}
                    fieldName={ATTACHMENT_FIELD_NAME}
                    currentFileIndex={index}
                    setFiles={() => handleRemoveAttachment(index)}
                    isLoading={isUploading && file instanceof File}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </SideDrawer>
    </div>
  );
};

export default ApproveDeficiencyDrawer;
